import { z } from "zod"
import {
  PRODUCT_VISUAL_PIM_COMPLETION_FAILURE,
  productVisualPimCompletionOutcomeSchema,
} from "./product-visual-pim-completion"

const count = z.number().int().nonnegative()
const identity = {
  store: z.string().min(1).max(100).nullable(),
  locale: z.string().min(1).max(20).nullable(),
  simpleSku: z.string().min(1).max(256).nullable(),
  magentoId: z.number().int().positive().nullable(),
}
const reason = z.enum(PRODUCT_VISUAL_PIM_COMPLETION_FAILURE)
export const productVisualPimCompletionEvidenceBoundarySchema = z.object(identity).strict()
export const productVisualPimCompletionWorkReferenceSchema = z
  .object({
    type: z.enum([
      "pim_event",
      "pim_delivery",
      "delivery_resolution",
      "pim_target",
      "fill_job",
      "fill_settlement",
      "fill_work",
      "replacement",
      "replacement_policy_history",
      "outbox",
      "render_plan",
      "render_job",
      "generation_job",
    ]),
    id: z.string().min(1).max(200),
    cause: z
      .string()
      .regex(/^[a-zA-Z0-9_.:-]{1,200}$/)
      .nullable(),
  })
  .strict()
export const productVisualPimCompletionTargetEvidenceSchema = z
  .object({
    store: z.string().min(1).max(100),
    simpleSku: z.string().min(1).max(256),
    magentoId: z.number().int().positive().nullable(),
    locale: z.string().min(1).max(20),
    ownerStore: z.string().min(1).max(100),
    observedAt: z.string().datetime(),
    imageState: z.enum(["current", "new", "stale", "excluded"]),
    work: z
      .object({
        pending: z.boolean(),
        actionable: z.boolean(),
        paused: z.boolean(),
        retryDue: z.boolean(),
        failedExhausted: z.boolean(),
      })
      .strict(),
    // Absent on historical saved targets; never inferred during readback.
    policyHistoryCount: count.optional(),
    reasons: z
      .array(z.object({ reason, occurrences: z.number().int().positive() }).strict())
      .max(14),
    agreement: z
      .object({
        contentAgreement: z.boolean(),
        templateAgreement: z.boolean(),
        outputAgreement: z.boolean(),
        fingerprintAgreement: z.boolean(),
      })
      .strict(),
    expectedFingerprint: z.string().max(200).nullable(),
    observedFingerprint: z.string().max(200).nullable(),
    gcs: z.enum(["not_attempted", "verified", "missing", "hash_mismatch", "unreadable"]),
    image: z
      .object({
        id: z.number().int().positive(),
        objectPath: z.string().min(1).max(2000),
        artifactHash: z.string().max(200).nullable(),
      })
      .strict()
      .nullable(),
    refusalCodes: z.array(z.string().regex(/^[a-zA-Z0-9_.:-]{1,200}$/)).max(100),
    inputGapCodes: z.array(z.string().regex(/^[a-zA-Z0-9_.:-]{1,200}$/)).max(100),
    missingWorkEvidence: z.boolean(),
    workReferences: z.array(productVisualPimCompletionWorkReferenceSchema),
  })
  .strict()
export const productVisualPimCompletionContextEvidenceSchema = z
  .object({
    ...identity,
    reason,
    occurrences: z.number().int().positive(),
    ownerStore: z.string().min(1).max(100).nullable(),
    observedAt: z.string().datetime(),
  })
  .strict()
export const PRODUCT_VISUAL_PIM_COMPLETION_EVIDENCE_COLLECTION = {
  targets: "targets",
  context: "context",
  work: "work",
} as const
export const productVisualPimCompletionFailuresQuerySchema = z
  .object({
    runId: z.uuid(),
    collection: z.enum(PRODUCT_VISUAL_PIM_COMPLETION_EVIDENCE_COLLECTION).default("targets"),
    targetId: z.coerce.number().int().positive().optional(),
    cursor: z.string().min(1).max(2000).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict()
  .refine(
    (input) =>
      input.collection === "work" ? input.targetId !== undefined : input.targetId === undefined,
    { message: "Only work pages require a target ID" }
  )
export const productVisualPimCompletionFailuresPageSchema = z
  .object({
    requestedRunId: z.uuid(),
    ownerRunId: z.uuid(),
    status: z.enum(["collecting", "partial", "complete", "historical_unavailable"]),
    collection: z.enum(PRODUCT_VISUAL_PIM_COMPLETION_EVIDENCE_COLLECTION),
    checkpoint: productVisualPimCompletionOutcomeSchema.nullable(),
    boundary: productVisualPimCompletionEvidenceBoundarySchema.nullable(),
    scanFinished: z.boolean(),
    sealedAt: z.string().datetime().nullable(),
    counts: z
      .object({ targets: count, context: count, work: count, reasonOccurrences: count })
      .strict(),
    targets: z
      .array(
        productVisualPimCompletionTargetEvidenceSchema
          .omit({ workReferences: true })
          .extend({ evidenceId: z.number().int().positive(), workCount: count })
      )
      .max(100),
    context: z
      .array(
        productVisualPimCompletionContextEvidenceSchema.extend({
          evidenceId: z.number().int().positive(),
        })
      )
      .max(100),
    work: z
      .array(
        productVisualPimCompletionWorkReferenceSchema.extend({
          evidenceId: z.number().int().positive(),
          targetId: z.number().int().positive(),
        })
      )
      .max(100),
    nextCursor: z.string().max(2000).nullable(),
  })
  .strict()
export type ProductVisualPimCompletionTargetEvidence = z.infer<
  typeof productVisualPimCompletionTargetEvidenceSchema
>
export type ProductVisualPimCompletionContextEvidence = z.infer<
  typeof productVisualPimCompletionContextEvidenceSchema
>
export type ProductVisualPimCompletionWorkReference = z.infer<
  typeof productVisualPimCompletionWorkReferenceSchema
>
export type ProductVisualPimCompletionEvidenceBoundary = z.infer<
  typeof productVisualPimCompletionEvidenceBoundarySchema
>
export type ProductVisualPimCompletionFailuresPage = z.infer<
  typeof productVisualPimCompletionFailuresPageSchema
>
export type ProductVisualPimCompletionFailuresQuery = z.input<
  typeof productVisualPimCompletionFailuresQuerySchema
>

export function getProductVisualPimCompletionOwnerId(input: {
  run: { id: string; coalescedIntoRunId?: string | null | undefined }
  visitedRunIds: ReadonlySet<string>
  ownerHops: number
}): string | null {
  const ownerId = input.run.coalescedIntoRunId
  if (
    !ownerId ||
    !z.uuid().safeParse(ownerId).success ||
    input.visitedRunIds.has(ownerId) ||
    input.ownerHops >= 8
  ) {
    return null
  }
  return ownerId
}
